import { PublicClientApplication, type Configuration } from '@azure/msal-browser';

/**
 * Microsoft Entra login via MSAL popup. We use only @azure/msal-browser (no
 * react wrapper) and return the ID token, which the backend validates against
 * the Entra tenant (audience = clientId). Config comes from /public/oauth-config.
 */

let instance: PublicClientApplication | null = null;
let instanceKey = '';

function getInstance(clientId: string, tenantId: string): PublicClientApplication {
  const key = `${clientId}:${tenantId}`;
  if (!instance || instanceKey !== key) {
    const cfg: Configuration = {
      auth: {
        clientId,
        authority: `https://login.microsoftonline.com/${tenantId}`,
        redirectUri: window.location.origin,
        postLogoutRedirectUri: window.location.origin,
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
      },
    };
    instance = new PublicClientApplication(cfg);
    instanceKey = key;
  }
  return instance;
}

export interface MicrosoftLoginResult {
  token: string;
  email?: string;
  name?: string;
}

export async function loginWithMicrosoft(
  clientId: string,
  tenantId: string,
): Promise<MicrosoftLoginResult> {
  const pca = getInstance(clientId, tenantId);
  await pca.initialize();

  const result = await pca.loginPopup({ scopes: ['openid', 'profile', 'email'] });
  if (!result.idToken) {
    throw new Error('Microsoft sign-in did not return an ID token');
  }

  return {
    token: result.idToken,
    email: result.account?.username,
    name: result.account?.name,
  };
}

export async function logoutMicrosoft(): Promise<void> {
  if (!instance) return;
  const accounts = instance.getAllAccounts();
  if (accounts.length === 0) return;
  try {
    await instance.logoutPopup({ account: accounts[0] });
  } catch {
    // best-effort; ignore popup-closed/cancel errors
  }
}
